/**
 * A mock implementation of the BANT strategy that includes simulation capabilities.
 * This strategy handles welcome/reconnection messages and provides
 * full conversation simulation capabilities.
 */
import type {
  ConversationConfig,
  RecapItem,
  TranscriptEvent,
} from "../../models/ScriptItemTypes";
import type {
  ConversationAnalyzer,
  ScriptAnalysisResult,
} from "../../services/ConversationService";
import type {
  BANTQualificationStrategy,
  SimulationCapableStrategy,
} from "../ConversationStrategy";

/**
 * A fully-featured mock BANT qualification strategy that includes
 * simulation capabilities for testing without a real API
 */
export class MockBANTStrategy
  implements
    BANTQualificationStrategy,
    SimulationCapableStrategy,
    ConversationAnalyzer
{
  constructor(private readonly debug = false) {}

  getName(): string {
    return "BANT Qualification (Mock)";
  }

  /**
   * Generates a personalized welcome message based on configuration
   */
  async generateWelcomeMessage(
    config: ConversationConfig,
  ): Promise<ScriptAnalysisResult> {
    const { sdr } = config;

    // Use provided template or default greeting
    const template =
      config.greetingTemplate ??
      "Hello, I'm {name} from {company}. I'm here to learn more about your needs and see how we might be able to help. Could you tell me what brought you to this call today?";

    // Replace template variables
    const text = template
      .replace("{name}", sdr.name)
      .replace("{company}", sdr.company ?? "our company");

    return {
      shouldGenerateScriptItem: true,
      suggestedScript: {
        text,
        tags: ["welcome", "greeting", "intro"],
      },
      suggestedWarmUp:
        "Listen carefully to understand their primary need or pain point.",
      detectedIntent: "greeting",
      confidence: 1.0,
    };
  }

  /**
   * Generates a personalized reconnection message
   */
  async generateReconnectionMessage(
    config: ConversationConfig,
  ): Promise<ScriptAnalysisResult> {
    const { sdr } = config;

    // Use provided template or default reconnection message
    const template =
      config.reconnectionTemplate ??
      "Sorry about that interruption. I'm {name} from {company} and we just reconnected. Let's continue where we left off.";

    // Replace template variables
    const text = template
      .replace("{name}", sdr.name)
      .replace("{company}", sdr.company ?? "our company");

    return {
      shouldGenerateScriptItem: true,
      suggestedScript: {
        text,
        tags: ["reconnection"],
      },
      detectedIntent: "reconnection",
      confidence: 1.0,
    };
  }

  /**
   * Analyzes a transcript event to determine if it requires a script suggestion
   */
  async analyze(
    event: TranscriptEvent,
    _context: TranscriptEvent[],
  ): Promise<ScriptAnalysisResult> {
    if (this.debug) {
      console.log("[MockBANTStrategy] Analyzing event:", event);
    }

    // Special case: If this is a metadata call with no real event, generate a greeting
    if (!event || event.id === "init") {
      // Use default template for now (in real usage, this would use config)
      return {
        shouldGenerateScriptItem: true,
        suggestedScript: {
          text: "Hi there! I'm your sales assistant today. Let's start by introducing yourself and asking what brings them to this conversation.",
          tags: ["greeting", "welcome"],
        },
        suggestedWarmUp:
          "After their response, ask about their specific challenges",
        confidence: 0.95,
        detectedIntent: "conversation_start",
      };
    }

    // Handle customer events for response suggestions
    if (event.speaker === "customer") {
      const text = event.text.toLowerCase();

      // Check for demo/feature related questions
      if (this.containsDemoKeywords(text)) {
        return this.generateDemoResponse();
      }

      // Check for buying signals first (important to catch these)
      if (this.containsBuyingSignals(text)) {
        return this.generateBuyingSignalResponse();
      }

      // Then check regular BANT keywords
      if (this.containsGreetingKeywords(text)) {
        return this.generateIntroResponse();
      }
      if (this.containsNeedKeywords(text)) {
        return this.generateNeedsResponse();
      }
      if (this.containsBudgetKeywords(text)) {
        return this.generateBudgetResponse();
      }
      if (this.containsTimelineKeywords(text)) {
        return this.generateTimelineResponse();
      }
      if (this.containsAuthorityKeywords(text)) {
        return this.generateAuthorityResponse();
      }

      // Default response if we can't categorize
      return {
        shouldGenerateScriptItem: true,
        suggestedScript: {
          text: "That's interesting. Could you tell me more about how this relates to your current challenges?",
          tags: ["follow-up", "discovery"],
        },
        suggestedWarmUp:
          "Look for opportunities to discuss budget or timeline next",
        confidence: 0.7,
      };
    }

    // Default response for non-customer events
    return { shouldGenerateScriptItem: false };
  }

  /**
   * Generate a response to demo/feature related questions
   */
  private generateDemoResponse(): ScriptAnalysisResult {
    return {
      shouldGenerateScriptItem: true,
      suggestedScript: {
        text: "Let me show you how this feature works. Our platform provides real-time guidance to your sales reps during calls, suggesting the right things to say at the right moment. Would you like to see a live demonstration?",
        tags: ["demo", "feature", "product"],
      },
      suggestedWarmUp: "Be prepared to demonstrate the key features",
      confidence: 0.9,
      detectedIntent: "demo_request",
    };
  }

  /**
   * Generate a response to introductory customer messages
   */
  private generateIntroResponse(): ScriptAnalysisResult {
    return {
      shouldGenerateScriptItem: true,
      suggestedScript: {
        text: "Thanks for reaching out today. I'd love to understand more about what specific challenges you're facing with your current solution?",
        tags: ["intro", "discovery"],
      },
      suggestedWarmUp: "Next, we'll want to explore their specific pain points",
      confidence: 0.9,
      detectedIntent: "introduction",
    };
  }

  /**
   * Generate a response to needs-related customer messages
   */
  private generateNeedsResponse(): ScriptAnalysisResult {
    return {
      shouldGenerateScriptItem: true,
      suggestedScript: {
        text: "I understand you're facing challenges with scaling. Could you tell me more about how this is impacting your business?",
        tags: ["needs", "discovery"],
      },
      suggestedWarmUp: "Ask about budget allocation for solving this issue",
      confidence: 0.85,
      detectedIntent: "needs_exploration",
    };
  }

  /**
   * Generate a response to budget-related customer messages
   */
  private generateBudgetResponse(): ScriptAnalysisResult {
    return {
      shouldGenerateScriptItem: true,
      suggestedScript: {
        text: "Thank you for sharing your budget. When would you ideally like to implement a solution to address these challenges?",
        tags: ["budget", "timeline"],
      },
      suggestedWarmUp: "Ask about the decision-making process",
      confidence: 0.8,
      detectedIntent: "budget_discussion",
    };
  }

  /**
   * Generate a response to timeline-related customer messages
   */
  private generateTimelineResponse(): ScriptAnalysisResult {
    return {
      shouldGenerateScriptItem: true,
      suggestedScript: {
        text: "Great, that timeline works with our implementation schedule. Who else is involved in the decision-making process for this purchase?",
        tags: ["timeline", "authority"],
      },
      suggestedWarmUp: "Prepare to summarize BANT criteria",
      confidence: 0.8,
      detectedIntent: "timeline_discussion",
    };
  }

  /**
   * Generate a response to authority-related customer messages
   */
  private generateAuthorityResponse(): ScriptAnalysisResult {
    return {
      shouldGenerateScriptItem: true,
      suggestedScript: {
        text: "Thanks for sharing that information. Let me recap what I've learned: [Summarize BANT elements]. Does that sound right?",
        tags: ["authority", "recap"],
      },
      confidence: 0.9,
      detectedIntent: "authority_discussion",
    };
  }

  /**
   * Generate a response to buying signals
   */
  private generateBuyingSignalResponse(): ScriptAnalysisResult {
    return {
      shouldGenerateScriptItem: true,
      suggestedScript: {
        text: "I'm glad to hear you're interested! The next step would be to schedule a demo with our technical team. Would you have time next week for that?",
        tags: ["buying_signal", "next_steps"],
      },
      suggestedWarmUp: "If they agree, suggest specific dates/times",
      confidence: 0.95,
      detectedIntent: "buying_signal",
    };
  }

  /**
   * Generates a recap of the BANT elements in the conversation
   */
  async generateRecap(events: TranscriptEvent[]): Promise<RecapItem[]> {
    if (this.debug) {
      console.log(
        "[MockBANTStrategy] Generating recap from events:",
        events.length,
      );
    }

    const budget = (await this.extractBudgetInfo(events)) ?? null;
    const authority = (await this.extractAuthorityInfo(events)) ?? null;
    const needs = await this.extractNeedsInfo(events);
    const timeline = await this.extractTimelineInfo(events);

    const recap: RecapItem[] = [];

    if (needs) recap.push(needs);
    if (budget) recap.push(budget);
    if (authority) recap.push(authority);
    if (timeline) recap.push(timeline);

    return recap;
  }

  /**
   * Extracts budget information from transcript events
   */
  async extractBudgetInfo(
    events: TranscriptEvent[],
  ): Promise<RecapItem | null> {
    const customerEvents = events.filter((e) => e.speaker === "customer");

    // Simple pattern matching for budget information
    const budgetInfo = customerEvents.find((e) =>
      this.containsBudgetKeywords(e.text.toLowerCase()),
    );

    if (!budgetInfo) {
      return {
        title: "Budget",
        description: "Not discussed",
        status: "not_discussed",
      };
    }

    // Extract dollar amount if present
    const dollarMatch = budgetInfo.text.match(/\$\s*(\d[\d,]*)/);
    const amount = dollarMatch
      ? dollarMatch[0]
      : "Discussed but amount unclear";

    return {
      title: "Budget",
      description: amount,
      status: "confirmed",
    };
  }

  /**
   * Extracts authority information from transcript events
   */
  async extractAuthorityInfo(
    events: TranscriptEvent[],
  ): Promise<RecapItem | null> {
    const customerEvents = events.filter((e) => e.speaker === "customer");

    const authorityInfo = customerEvents.find((e) =>
      this.containsAuthorityKeywords(e.text.toLowerCase()),
    );

    if (!authorityInfo) {
      return {
        title: "Authority",
        description: "Not discussed",
        status: "not_discussed",
      };
    }

    // Determine level of authority
    const text = authorityInfo.text.toLowerCase();
    let description = "Role in decision process unclear";

    if (text.includes("final decision") || text.includes("decision maker")) {
      description = "Final decision maker";
    } else if (text.includes("influence") || text.includes("input")) {
      description = "Influencer in decision process";
    }

    return {
      title: "Authority",
      description,
      status: "confirmed",
    };
  }

  /**
   * Extracts needs information from transcript events
   */
  async extractNeedsInfo(events: TranscriptEvent[]): Promise<RecapItem | null> {
    const customerEvents = events.filter((e) => e.speaker === "customer");

    const needsInfo = customerEvents.find((e) =>
      this.containsNeedKeywords(e.text.toLowerCase()),
    );

    if (!needsInfo) {
      return {
        title: "Need",
        description: "Not clearly identified",
        status: "not_discussed",
      };
    }

    // Generalize the need
    const text = needsInfo.text.toLowerCase();
    let description = "General improvement needed";

    if (text.includes("scale") || text.includes("growth")) {
      description = "Scaling challenges";
    } else if (text.includes("cost") || text.includes("expensive")) {
      description = "Cost reduction needed";
    } else if (text.includes("time") || text.includes("slow")) {
      description = "Efficiency improvement";
    }

    return {
      title: "Need",
      description,
      status: "confirmed",
    };
  }

  /**
   * Extracts timeline information from transcript events
   */
  async extractTimelineInfo(
    events: TranscriptEvent[],
  ): Promise<RecapItem | null> {
    const customerEvents = events.filter((e) => e.speaker === "customer");

    const timelineInfo = customerEvents.find((e) =>
      this.containsTimelineKeywords(e.text.toLowerCase()),
    );

    if (!timelineInfo) {
      return {
        title: "Timeline",
        description: "Not discussed",
        status: "not_discussed",
      };
    }

    // Extract timeframe if present
    const text = timelineInfo.text.toLowerCase();
    let description = "Timeline mentioned but details unclear";

    if (
      text.includes("quarter") ||
      text.includes("q1") ||
      text.includes("q2") ||
      text.includes("q3") ||
      text.includes("q4")
    ) {
      description = "Quarterly timeframe";
    } else if (text.includes("month") || text.includes("week")) {
      description = "Near-term implementation";
    } else if (text.includes("year") || text.includes("annual")) {
      description = "Long-term implementation";
    }

    return {
      title: "Timeline",
      description,
      status: "confirmed",
    };
  }

  /**
   * Determines the next BANT element to focus on
   */
  async suggestNextBANTElement(
    events: TranscriptEvent[],
  ): Promise<"budget" | "authority" | "need" | "timeline" | "recap"> {
    // Simple implementation based on conversation length
    if (events.length <= 3) {
      return "need";
    }
    if (events.length <= 6) {
      return "budget";
    }
    if (events.length <= 9) {
      return "authority";
    }
    if (events.length <= 12) {
      return "timeline";
    }
    return "recap";
  }

  // Helper methods for keyword detection
  private containsGreetingKeywords(text: string): boolean {
    const keywords = [
      "hello",
      "hi",
      "hey",
      "greetings",
      "good morning",
      "good afternoon",
      "thanks for",
    ];
    return keywords.some((keyword) => text.includes(keyword));
  }

  private containsNeedKeywords(text: string): boolean {
    const keywords = [
      "problem",
      "challenge",
      "issue",
      "need",
      "want",
      "looking for",
      "improve",
      "optimize",
      "scale",
      "growth",
      "difficult",
    ];
    return keywords.some((keyword) => text.includes(keyword));
  }

  private containsBudgetKeywords(text: string): boolean {
    const keywords = [
      "budget",
      "cost",
      "price",
      "investment",
      "spend",
      "funding",
      "afford",
      "expensive",
      "cheap",
      "$",
      "dollar",
      "money",
    ];
    return keywords.some((keyword) => text.includes(keyword));
  }

  private containsTimelineKeywords(text: string): boolean {
    const keywords = [
      "timeline",
      "deadline",
      "when",
      "schedule",
      "asap",
      "urgent",
      "soon",
      "month",
      "quarter",
      "year",
      "date",
      "time",
      "q1",
      "q2",
      "q3",
      "q4",
    ];
    return keywords.some((keyword) => text.includes(keyword));
  }

  private containsAuthorityKeywords(text: string): boolean {
    const keywords = [
      "decision",
      "approve",
      "authority",
      "boss",
      "manager",
      "director",
      "team",
      "committee",
      "board",
      "approval",
      "sign off",
    ];
    return keywords.some((keyword) => text.includes(keyword));
  }

  private containsBuyingSignals(text: string): boolean {
    const buyingSignalPhrases = [
      "interested",
      "sounds good",
      "like that",
      "that works",
      "good fit",
      "next steps",
      "how soon",
      "when can we",
      "start",
      "implement",
      "let's do it",
      "sign up",
      "sign me up",
      "tell me more",
      "sounds like exactly what",
      "perfect",
      "price",
      "proposal",
      "agreement",
      "contract",
      "moving forward",
    ];
    return buyingSignalPhrases.some((phrase) => text.includes(phrase));
  }

  private containsDemoKeywords(text: string): boolean {
    const demoKeywords = [
      "demo",
      "demonstrate",
      "show me",
      "how does it work",
      "feature",
      "functionality",
      "works",
      "real-time",
      "see it",
      "example",
      "presentation",
      "showcase",
      "walk through",
      "tour",
      "preview",
    ];
    return demoKeywords.some((keyword) => text.includes(keyword));
  }

  /**
   * SIMULATION CAPABILITIES
   */

  /**
   * Identifies the current conversation stage based on history
   */
  private async identifyConversationStage(
    events: TranscriptEvent[],
  ): Promise<string> {
    if (events.length <= 2) {
      return "initial_greeting";
    }

    // Get the text from the last few messages
    const recentMessages = events.slice(-3);
    const recentText = recentMessages
      .map((e) => e.text)
      .join(" ")
      .toLowerCase();

    // Check for stage-specific keywords in recent messages
    if (this.containsBudgetKeywords(recentText)) {
      return "budget_exploration";
    }

    if (this.containsAuthorityKeywords(recentText)) {
      return "authority_check";
    }

    if (this.containsTimelineKeywords(recentText)) {
      return "timeline_discussion";
    }

    if (this.containsBuyingSignals(recentText)) {
      return "closing";
    }

    // If no specific stage is detected from recent messages,
    // use conversation length as a fallback
    const messageCount = events.length;
    if (messageCount <= 4) {
      return "needs_identification";
    }
    if (messageCount <= 7) {
      return "budget_exploration";
    }
    if (messageCount <= 10) {
      return "authority_check";
    }
    if (messageCount <= 13) {
      return "timeline_discussion";
    }
    return "closing";
  }

  /**
   * Simulates a customer response to an SDR statement
   */
  async simulateCustomerResponse(
    sdrStatement: string,
    conversationHistory: TranscriptEvent[],
  ): Promise<string> {
    // Get the current conversation stage
    const stage = await this.identifyConversationStage(conversationHistory);

    // Check if the SDR's statement contains stage-specific keywords
    const sdrText = sdrStatement.toLowerCase();

    // If SDR asks about budget, respond about budget regardless of stage
    if (this.containsBudgetKeywords(sdrText)) {
      return "Yes, we've allocated about $50,000 for the right solution that can address these challenges.";
    }

    // If SDR asks about authority, respond about authority
    if (this.containsAuthorityKeywords(sdrText)) {
      return "I'll be leading the evaluation process, but the final decision will involve our VP of Sales and our CTO since it impacts both teams.";
    }

    // If SDR asks about timeline, respond about timeline
    if (this.containsTimelineKeywords(sdrText)) {
      return "We're looking to implement something by the end of this quarter, so within about 2 months.";
    }

    // Default responses based on stage
    switch (stage) {
      case "initial_greeting":
        return "Thanks for reaching out. We've been having issues with our current process and heard your solution might help.";

      case "needs_identification":
        return "Our main challenge is that our sales team spends too much time on administrative tasks instead of selling. We need to automate our workflow and improve visibility.";

      case "budget_exploration":
        return "Yes, we've allocated about $50,000 for the right solution that can address these challenges.";

      case "authority_check":
        return "I'll be leading the evaluation process, but the final decision will involve our VP of Sales and our CTO since it impacts both teams.";

      case "timeline_discussion":
        return "We're looking to implement something by the end of this quarter, so within about 2 months.";

      case "closing":
        return "Yes, Tuesday at 2pm works for me. Should I invite our VP of Sales to that call as well?";

      default:
        return "That's interesting. Can you tell me more about how your solution addresses that specific challenge?";
    }
  }

  /**
   * Generates the next likely SDR statement based on the conversation flow
   */
  async suggestNextSDRStatement(
    customerResponse: string,
    conversationHistory: TranscriptEvent[],
  ): Promise<string> {
    // Get the current conversation stage
    const stage = await this.identifyConversationStage(conversationHistory);

    // Generate stage-appropriate responses
    switch (stage) {
      case "initial_greeting":
        return "I understand you're facing challenges with scaling. Could you tell me more about how this is impacting your business?";

      case "needs_identification":
        return "That's a common challenge. What's your current budget for addressing these issues?";

      case "budget_exploration":
        return "Great, that's helpful context. Who else will be involved in the decision-making process?";

      case "authority_check":
        return "Perfect, and what's your timeline for implementing a solution?";

      case "timeline_discussion":
        return "That's a reasonable timeline. Would you be interested in scheduling a demo to see how our solution can help?";

      case "closing":
        return "That would be great! I'll send over a calendar invite for Tuesday at 2pm.";

      default:
        return "That's interesting. Can you tell me more about how your solution addresses that specific challenge?";
    }
  }

  /**
   * Runs a complete simulated conversation
   */
  async runSimulation(
    config: ConversationConfig,
    maxTurns = 10,
  ): Promise<TranscriptEvent[]> {
    if (this.debug) {
      console.log(
        `[MockBANTStrategy] Starting simulation with ${maxTurns} turns`,
      );
    }

    const conversation: TranscriptEvent[] = [];

    // Start with system message
    conversation.push({
      id: "1",
      timestamp: Date.now(),
      speaker: "system",
      text: "conversation_start",
    });

    // Generate welcome message from SDR
    const welcomeResult = await this.generateWelcomeMessage(config);
    if (
      welcomeResult.shouldGenerateScriptItem &&
      welcomeResult.suggestedScript?.text
    ) {
      conversation.push({
        id: "2",
        timestamp: Date.now() + 1000,
        speaker: "sdr",
        text: welcomeResult.suggestedScript.text,
      });
    }

    // Define conversation stages in order
    const stages = [
      "needs_identification",
      "budget_exploration",
      "authority_check",
      "timeline_discussion",
      "closing",
    ];

    // Run through each stage
    for (const stage of stages) {
      // SDR asks about the current stage
      const sdrQuestion = await this.generateStageQuestion(stage);
      conversation.push({
        id: (conversation.length + 1).toString(),
        timestamp: Date.now() + conversation.length * 1000,
        speaker: "sdr",
        text: sdrQuestion,
      });

      // Customer responds to the stage question
      const customerResponse = await this.generateStageResponse(stage);
      conversation.push({
        id: (conversation.length + 1).toString(),
        timestamp: Date.now() + conversation.length * 1000,
        speaker: "customer",
        text: customerResponse,
      });

      // Break if we've reached maxTurns
      if (conversation.length >= maxTurns + 2) {
        // +2 for system and greeting
        break;
      }
    }

    if (this.debug) {
      console.log(
        `[MockBANTStrategy] Completed simulation with ${conversation.length} events`,
      );
    }

    return conversation;
  }

  /**
   * Generates a question appropriate for the current conversation stage
   */
  private async generateStageQuestion(stage: string): Promise<string> {
    switch (stage) {
      case "needs_identification":
        return "I understand you're facing challenges with scaling. Could you tell me more about how this is impacting your business?";

      case "budget_exploration":
        return "That's helpful context. What's your budget range for implementing a solution like this?";

      case "authority_check":
        return "Great, and who else will be involved in the decision-making process?";

      case "timeline_discussion":
        return "Perfect. What's your timeline for implementing a solution?";

      case "closing":
        return "Would you like to schedule a demo to see how our solution can help?";

      default:
        return "Could you tell me more about that?";
    }
  }

  /**
   * Generates a response appropriate for the current conversation stage
   */
  private async generateStageResponse(stage: string): Promise<string> {
    switch (stage) {
      case "needs_identification":
        return "Our main challenge is that our sales team spends too much time on administrative tasks instead of selling. We need to automate our workflow and improve visibility.";

      case "budget_exploration":
        return "We've allocated about $50,000 for the right solution that can address these challenges.";

      case "authority_check":
        return "I'll be leading the evaluation process, but the final decision will involve our VP of Sales and our CTO since it impacts both teams.";

      case "timeline_discussion":
        return "We're looking to implement something by the end of this quarter, so within about 2 months.";

      case "closing":
        return "Yes, Tuesday at 2pm works for me. Should I invite our VP of Sales to that call as well?";

      default:
        return "That's interesting. Can you tell me more about how your solution addresses that specific challenge?";
    }
  }

  /**
   * Implementation of ConversationAnalyzer.analyzeTranscriptEvent
   * Use the existing 'analyze' method to fulfill this interface
   */
  async analyzeTranscriptEvent(
    event: TranscriptEvent,
  ): Promise<ScriptAnalysisResult> {
    return this.analyze(event, [event]); // Use minimal context with just the current event
  }

  /**
   * Implementation of ConversationAnalyzer.generateRecapItems
   * Use the existing 'generateRecap' method to fulfill this interface
   */
  async generateRecapItems(events: TranscriptEvent[]): Promise<RecapItem[]> {
    return this.generateRecap(events);
  }

  /**
   * Complete the call
   */
  async completeCall(): Promise<void> {
    console.log("[MockBANTStrategy] Call completed");
  }
}
